/**
 * graphLayout — places the knowledge graph as a radial tree around the root (mockup screen 1).
 * Each subtree gets a slice of the circle sized by its leaf count, so a container with many
 * files does not crowd a small one. Chat memories sit on the inner ring like containers.
 * Pure function: same nodes in → same positions out (the tests pin this).
 */
import type { KNode } from './knowledgeApi';

export interface Placed { x: number; y: number; depth: number; color: string }

export const RING = [0, 130, 215, 290];

/** Container colours from the mockup: dot · branch diamond · file square. */
export const PALETTE = [
  { c: '#2E6B4E', b: '#4D9A75', f: '#86EFAC' },
  { c: '#B45309', b: '#F59E0B', f: '#FBBF24' },
  { c: '#1D4ED8', b: '#60A5FA', f: '#93C5FD' },
  { c: '#7C3AED', b: '#A78BFA', f: '#C4B5FD' },
];
export const MEMORY_COLOR = '#0F766E';

export function childrenOf(nodes: KNode[]): Map<string | null, KNode[]> {
  const ids = new Set(nodes.map((n) => n.id));
  const map = new Map<string | null, KNode[]>();
  for (const n of nodes) {
    // A parent we were not sent (e.g. filtered by `since`) → hang it on the root so it still shows.
    const p = n.type === 'root' ? null : n.parentId && ids.has(n.parentId) ? n.parentId : 'root';
    if (!map.has(p)) map.set(p, []);
    map.get(p)!.push(n);
  }
  return map;
}

/** Index of the container a node lives under (for its colour); -1 for root and memories. */
export function containerIndex(nodes: KNode[]): Map<string, number> {
  const kids = childrenOf(nodes);
  const out = new Map<string, number>();
  const containers = (kids.get('root') ?? []).filter((n) => n.type === 'container');
  containers.forEach((c, i) => {
    const stack = [c];
    while (stack.length) {
      const n = stack.pop()!;
      out.set(n.id, i % PALETTE.length);
      stack.push(...(kids.get(n.id) ?? []));
    }
  });
  return out;
}

export function colorOf(n: KNode, idx: number | undefined): string {
  if (n.type === 'memory') return MEMORY_COLOR;
  if (n.type === 'root' || idx === undefined) return '#E2A35B';
  const p = PALETTE[idx];
  return n.type === 'container' ? p.c : n.type === 'branch' ? p.b : p.f;
}

export function layoutGraph(nodes: KNode[], cx = 310, cy = 235): Map<string, Placed> {
  const kids = childrenOf(nodes);
  const idx = containerIndex(nodes);
  const pos = new Map<string, Placed>();
  const root = nodes.find((n) => n.type === 'root');
  const leaves = new Map<string, number>();
  const weigh = (n: KNode): number => {
    const k = kids.get(n.id) ?? [];
    const w = k.length === 0 ? 1 : k.reduce((s, c) => s + weigh(c), 0);
    leaves.set(n.id, w);
    return w;
  };
  const place = (n: KNode, depth: number, a0: number, a1: number) => {
    const a = (a0 + a1) / 2;
    const r = RING[Math.min(depth, RING.length - 1)] + (depth >= RING.length ? (depth - RING.length + 1) * 60 : 0);
    pos.set(n.id, { x: Math.round(cx + r * Math.cos(a)), y: Math.round(cy + r * Math.sin(a)), depth, color: colorOf(n, idx.get(n.id)) });
    const k = kids.get(n.id) ?? [];
    const total = k.reduce((s, c) => s + (leaves.get(c.id) ?? 1), 0) || 1;
    let at = a0;
    for (const c of k) {
      const span = ((a1 - a0) * (leaves.get(c.id) ?? 1)) / total;
      place(c, depth + 1, at, at + span);
      at += span;
    }
  };
  if (root) {
    weigh(root);
    // Start at the top-left so the first container is not hidden behind the legend.
    place(root, 0, -Math.PI * 0.75, Math.PI * 1.25);
  } else {
    // No root sent: lay the top-level nodes out as if there were one.
    const top = kids.get('root') ?? kids.get(null) ?? [];
    const fake: KNode = { id: 'root', type: 'root', label: '', parentId: null };
    kids.set('root', top);
    weigh(fake);
    place(fake, 0, -Math.PI * 0.75, Math.PI * 1.25);
    pos.delete('root');
  }
  return pos;
}
